import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, combineLatest, distinctUntilChanged, filter, map } from 'rxjs';

import { selectIsLinearBetslip } from '../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../base/store/state';
import { ISystemSlipTypeInfo } from '../../models';
import { systemBetPickOddStateSelector } from '../../selectors';
import SystemBetTypesCalculatorService from './system-bet-types-calculator.service';

export enum SystemTypeOrder {
    ByBetCount = 'ByBetCount',
    ByIndex = 'ByIndex',
}

@Injectable()
export default class SystemBetTypesService {
    private systemTypesByIndex$: Observable<ISystemSlipTypeInfo[]>;
    private systemTypesByBetCount$: Observable<ISystemSlipTypeInfo[]>;

    constructor(
        private typesCalculator: SystemBetTypesCalculatorService,
        private store: Store<IBetslipRootState>,
    ) {
        const systemBetInfo$ = this.store.select(systemBetPickOddStateSelector).pipe(filter((picks) => !!picks));
        const isLinear$ = this.store.select(selectIsLinearBetslip);

        const systemTypes$ = combineLatest([isLinear$, systemBetInfo$]).pipe(
            map(([isLinear, picks]) => this.typesCalculator.calculateAvailableTypes(picks, isLinear)),

            distinctUntilChanged((a, b) => {
                const lengthChanged = a.length !== b.length;

                return (
                    !lengthChanged &&
                    b.every((current) =>
                        a.some(
                            (previous) =>
                                previous.typeInfo.slipType.key === current.typeInfo.slipType.key &&
                                previous.typeInfo.typeName === current.typeInfo.typeName &&
                                previous.typeInfo.betCount === current.typeInfo.betCount,
                        ),
                    )
                );
            }),
        );

        this.systemTypesByBetCount$ = systemTypes$.pipe(
            map((types) =>
                types
                    // in case of banker, the order will be -1 and it should always come first
                    .sort((x, y) => (x.order === -1 ? x.order : x.typeInfo.betCount - y.typeInfo.betCount))
                    .map((type) => type.typeInfo),
            ),
        );

        this.systemTypesByIndex$ = systemTypes$.pipe(map((types) => types.sort((x, y) => x.order - y.order).map((type) => type.typeInfo)));
    }

    getAvailableSystemTypes$(order: SystemTypeOrder): Observable<ISystemSlipTypeInfo[]> {
        if (order === SystemTypeOrder.ByBetCount) {
            return this.systemTypesByBetCount$;
        }

        return this.systemTypesByIndex$;
    }
}
